// src/admin/components/AdminHeader.jsx
//
// Header for the Admin / Founder Dashboard:
// title, subtitle, date selector and logout button.

import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  formatDateYMD,
  parseYMD,
  addDays,
  isSameDay,
  formatDate
} from './adminFormatters';


function IconChevronLeft(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 18l-6-6 6-6"
      />
    </svg>
  );
}


function IconChevronRight(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 18l6-6-6-6"
      />
    </svg>
  );
}


function IconTrendingUp(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M23 6l-9.5 9.5-5-5L1 18"
      />

      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 6h6v6"
      />
    </svg>
  );
}


function IconLogout(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
      />

      <polyline
        strokeLinecap="round"
        strokeLinejoin="round"
        points="16 17 21 12 16 7"
      />

      <line
        strokeLinecap="round"
        strokeLinejoin="round"
        x1="21"
        y1="12"
        x2="9"
        y2="12"
      />
    </svg>
  );
}


export default function AdminHeader({
  selectedDate,
  onDateChange
}) {

  const navigate = useNavigate();


  // =========================================================
  // DATE HANDLERS
  // =========================================================

  const handlePrev = () => {
    onDateChange(
      addDays(selectedDate, -1)
    );
  };


  const handleNext = () => {
    onDateChange(
      addDays(selectedDate, 1)
    );
  };


  const handleToday = () => {
    onDateChange(
      new Date()
    );
  };


  const handleInputChange = (e) => {

    if (!e.target.value) {
      return;
    }

    onDateChange(
      parseYMD(e.target.value)
    );
  };


  // =========================================================
  // LOGOUT
  // =========================================================

  const handleLogout = () => {

    /*
     * IMPORTANT:
     *
     * Use the same localStorage key that your existing
     * login/authentication code uses.
     *
     * If your application stores the token as "token",
     * the below is correct.
     */

    localStorage.removeItem('token');

    /*
     * If your application stores user information separately,
     * remove that as well.
     *
     * Remove this line if you don't have "user" in localStorage.
     */
    localStorage.removeItem('user');


    /*
     * Optional:
     * If you use another authentication key, remove it here.
     *
     * Example:
     *
     * localStorage.removeItem('authToken');
     * localStorage.removeItem('accessToken');
     */


    // Redirect to login page
    navigate('/login');
  };


  const today = isSameDay(
    selectedDate,
    new Date()
  );


  // =========================================================
  // UI
  // =========================================================

  return (

    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

      {/* =====================================================
          LEFT SIDE
      ===================================================== */}

      <div className="flex items-center gap-3">

        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#11A8A4] text-white">

          <IconTrendingUp className="h-5 w-5" />

        </div>


        <div>

          <h1 className="text-2xl font-bold tracking-tight text-[#172B2B]">
            Admin Dashboard
          </h1>

          <p className="text-sm text-[#6B7C7C]">
            Founder / Financial Overview
          </p>

        </div>

      </div>


      {/* =====================================================
          RIGHT SIDE
      ===================================================== */}

      <div className="flex flex-wrap items-center gap-2">

        {/* ===================================================
            DATE NAVIGATION
        =================================================== */}

        <div className="flex items-center gap-1 rounded-lg border border-[#E2E8E8] bg-white p-1 shadow-sm">

          <button
            type="button"
            onClick={handlePrev}
            aria-label="Previous day"
            className="flex h-8 w-8 items-center justify-center rounded-md text-[#6B7C7C] transition-colors hover:bg-[#E8F8F7] hover:text-[#0D8F8C]"
          >

            <IconChevronLeft className="h-4 w-4" />

          </button>


          <span className="min-w-[9.5rem] px-2 text-center text-sm font-semibold text-[#172B2B]">

            {formatDate(selectedDate)}

          </span>


          <button
            type="button"
            onClick={handleNext}
            aria-label="Next day"
            className="flex h-8 w-8 items-center justify-center rounded-md text-[#6B7C7C] transition-colors hover:bg-[#E8F8F7] hover:text-[#0D8F8C]"
          >

            <IconChevronRight className="h-4 w-4" />

          </button>

        </div>


        {/* ===================================================
            TODAY
        =================================================== */}

        <button
          type="button"
          onClick={handleToday}
          disabled={today}
          className="rounded-lg border border-[#E2E8E8] bg-white px-3 py-2 text-sm font-medium text-[#172B2B] shadow-sm transition-colors hover:bg-[#E8F8F7] hover:text-[#0D8F8C] disabled:cursor-default disabled:opacity-50"
        >
          Today
        </button>


        {/* ===================================================
            DATE PICKER
        =================================================== */}

        <input
          type="date"
          value={formatDateYMD(selectedDate)}
          onChange={handleInputChange}
          className="rounded-lg border border-[#E2E8E8] bg-white px-2.5 py-2 text-sm text-[#172B2B] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#11A8A4]"
        />


        {/* ===================================================
            LOGOUT
        =================================================== */}

        <button
          type="button"
          onClick={handleLogout}
          aria-label="Logout"
          title="Logout"
          className="inline-flex items-center gap-2 rounded-lg border border-[#F1C7C7] bg-white px-3 py-2 text-sm font-semibold text-[#C94A4A] shadow-sm transition-colors hover:bg-[#FFF5F5] hover:text-[#B53D3D]"
        >

          <IconLogout className="h-4 w-4" />

          <span>
            Logout
          </span>

        </button>

      </div>

    </div>
  );
}